import React, {
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from 'react';
import AssignmentFeedback from 'models/assignments/AssignmentFeedback';
import { useAssignmentFeedbackContext } from 'contexts/AssignmentFeedbackContext';

// This interface defines what parent components can call on this component
export interface FeedbackHighlighterHandles {
  handleFeedbackItemClick: (feedbackId: string) => void;
  getFeedback: () => AssignmentFeedback[];
}

interface FeedbackHighlighterProps {
  submissionId: string;
  contentText: string;
  formatTextWithHighlights: (
    text: string,
    activeFeedbackId: string | null,
    feedback: AssignmentFeedback[],
  ) => string;
  onFeedbackLoaded?: (feedback: AssignmentFeedback[]) => void;
  onActiveFeedbackChanged?: (feedbackId: string | null) => void;
}

/**
 * A reusable component that handles feedback highlighting for any content type
 *
 * This component:
 * 1. Loads feedback from the API
 * 2. Manages which feedback item is currently active
 * 3. Exposes methods for external control via forwardRef
 * 4. Uses a provided formatter function to apply highlighting to content
 */
const FeedbackHighlighter = forwardRef<
  FeedbackHighlighterHandles,
  FeedbackHighlighterProps
>(
  (
    {
      contentText,
      formatTextWithHighlights,
      onFeedbackLoaded,
      onActiveFeedbackChanged,
    },
    ref,
  ) => {
    const { feedback } = useAssignmentFeedbackContext();
    const [activeFeedbackId, setActiveFeedbackId] = useState<string | null>(
      null,
    );
    const [formattedContent, setFormattedContent] = useState<string>('');

    // Load feedback from API
    useEffect(() => {
      if (!feedback.length) return;

      if (onFeedbackLoaded) {
        onFeedbackLoaded(feedback);
      }
    }, [feedback, onFeedbackLoaded]);

    // Format content with highlights when feedback or active ID changes
    useEffect(() => {
      if (!contentText) return;

      const formatted = formatTextWithHighlights(
        contentText,
        activeFeedbackId,
        feedback,
      );
      setFormattedContent(formatted);
    }, [contentText, activeFeedbackId, feedback, formatTextWithHighlights]);

    // When active feedback ID changes, notify parent
    useEffect(() => {
      if (onActiveFeedbackChanged) {
        onActiveFeedbackChanged(activeFeedbackId);
      }
    }, [activeFeedbackId, onActiveFeedbackChanged]);

    // Handle feedback item click (exposed to parent via ref)
    const handleFeedbackItemClick = (feedbackId: string) => {
      if (activeFeedbackId === feedbackId) {
        setActiveFeedbackId(null);
      } else {
        setActiveFeedbackId(feedbackId);
      }
    };

    // Expose methods to parent components
    useImperativeHandle(ref, () => ({
      handleFeedbackItemClick,
      getFeedback: () => feedback,
    }));

    return (
      <>
        <div
          className="highlighted-content"
          dangerouslySetInnerHTML={{ __html: formattedContent }}
        />
      </>
    );
  },
);

export default FeedbackHighlighter;
